import React from 'react';
import * as yup from 'yup';
import { Box } from '@mui/material';
import Gallery from './Gallery';
import Thumbnail from './Thumbnail';
import VideoLoom from './VideoLoom';
import SettingsFooter from '../SparkSettingsFooter';
import { SparkForm } from 'sharedComponents/SparkForm';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import type { SparkGalleryImagesType } from 'sharedComponents/SparkForm/SparkFormImageGallery';
import { VIDEO_URL_PATTERN } from 'constants/regex';

export type ImagesAndMediaFormValues = {
  imageURL: string;
  sparkImages: SparkGalleryImagesType[];
  videoURL: string;
};

type ImagesAndMediaFormProps = {
  joinedSpark: JoinedSparks | undefined;
  onSave: (values: ImagesAndMediaFormValues) => Promise<void>;
  setSelectedItemIndex: React.Dispatch<React.SetStateAction<number>>;
  onChangeThumbnail: (imageURL: string) => void;
};

function ImagesAndMediaForm({
  joinedSpark,
  setSelectedItemIndex,
  onSave,
  onChangeThumbnail,
}: ImagesAndMediaFormProps) {
  const joinedSparkAttributes = joinedSpark?.attributes;
  const sparkGalleryImages = joinedSpark?.sparkImages;

  const validationSchema = React.useMemo(() => {
    return yup.object({
      videoURL: yup
        .string()
        .matches(
          VIDEO_URL_PATTERN,
          'Please enter a valid YouTube or Loom share URL'
        ),
    });
  }, []);

  const handleSave = async (values: ImagesAndMediaFormValues) => {
    await onSave(values);
    setSelectedItemIndex(2);
  };

  const sparkImageUrls = React.useMemo(() => {
    return sparkGalleryImages?.map((image) => {
      const imageURL = image.attributes.imageURL;
      return {
        preview: imageURL,
      };
    });
  }, [sparkGalleryImages]);

  const initialValues = {
    imageURL: joinedSparkAttributes?.iconURL || '',
    sparkImages: sparkImageUrls || [],
    videoURL: joinedSparkAttributes?.videoURL || '',
  };

  return (
    <SparkForm<ImagesAndMediaFormValues>
      initialValues={initialValues}
      onSave={handleSave}
      validationSchema={validationSchema}
      containerProps={{
        position: 'absolute',
      }}
    >
      <Box display='flex' flexDirection='column' width='100%' pr={1} pb={2}>
        <Thumbnail onChangeThumbnail={onChangeThumbnail} />
        <Gallery />
        <VideoLoom />
        <SettingsFooter primaryButtonText='Next Step: Publish Checklist' />
      </Box>
    </SparkForm>
  );
}

export default ImagesAndMediaForm;
